// SurveyDashboard.js — Nebraska Panhandle Community Survey Results
// Live data: fetched from Google Sheets CSV export on every page load.
// Adding new responses to the sheet is all that's needed to update the dashboard.
import React, { useState, useEffect } from "react";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  PieChart, Pie, Cell, ResponsiveContainer,
} from "recharts";

const C = {
  navy:    "#122326",
  slate:   "#213A3E",
  gold:    "#BF6847",
  goldLt:  "#D4956A",
  cream:   "#F0EFEB",
  warm:    "#F7F6F3",
  sand:    "#E2E0DB",
  white:   "#FFFFFF",
  serious: "#B94040",
  moderate:"#D4834A",
  minor:   "#6B9E6B",
  none:    "#9BB3B8",
};

// ── Survey data via Vercel API proxy (avoids CORS / Google auth issues) ──────
const SHEET_CSV_URL = "/api/survey-data";

// ── Column indices (0-based, from header row) ─────────────────────────────────
const COL = {
  TIMESTAMP:            0,
  COUNTY:               1,
  Q5_SENTIMENT:         5,
  Q6_STAY:              6,
  Q7_JOB:               7,
  Q7_HOUSING:           8,
  Q7_CHILDCARE:         9,
  Q7_INTERNET:         10,
  Q7_TRAINING:         11,
  Q7_BUSINESS:         12,
  Q7_YOUTH:            13,
  Q7_WATER:            14,
  Q10_SUCCESSION:      28,
  Q12_HOUSING_IMPACT:  35,
  Q13_CHILDCARE_IMPACT:36,
  Q15_FAMILIARITY:     45,
  Q18_JOBS:            53,
  Q18_LIVING:          54,
  Q18_PADD:            55,
};

// ── CSV parser (handles quoted fields with embedded commas) ───────────────────
function parseCSV(text) {
  const rows = [];
  const src = text.replace(/\r\n/g, "\n").replace(/\r/g, "\n");
  let row = [], cell = "", inQ = false;

  for (let i = 0; i < src.length; i++) {
    const ch = src[i];
    if (inQ) {
      if (ch === '"' && src[i + 1] === '"') { cell += '"'; i++; }
      else if (ch === '"')                   { inQ = false; }
      else                                   { cell += ch; }
    } else {
      if      (ch === '"')  { inQ = true; }
      else if (ch === ',')  { row.push(cell.trim()); cell = ""; }
      else if (ch === "\n") { row.push(cell.trim()); rows.push(row); row = []; cell = ""; }
      else                  { cell += ch; }
    }
  }
  if (cell || row.length) { row.push(cell.trim()); rows.push(row); }

  // Drop empty trailing rows
  return rows.filter(r => r.some(c => c !== ""));
}

// ── Aggregate all charts and stats from parsed rows ───────────────────────────
function aggregate(rows) {
  const N = rows.length;
  if (N === 0) return null;

  const count = (col, match) =>
    rows.filter(r => (r[col] || "").trim() === match).length;

  const startsWith = (col, prefix) =>
    rows.filter(r => (r[col] || "").trim().startsWith(prefix)).length;

  // ── Q1 County distribution ───────────────────────────────────────────────
  const countyCounts = {};
  rows.forEach(r => {
    const raw = (r[COL.COUNTY] || "").replace(" County", "").trim();
    const c = raw || "Unknown";
    countyCounts[c] = (countyCounts[c] || 0) + 1;
  });
  const countyData = Object.entries(countyCounts)
    .map(([county, n]) => ({ county, n }))
    .sort((a, b) => b.n - a.n);

  // Unique counties (excluding "Unknown" / "Outside/Ties")
  const countyCount = Object.keys(countyCounts)
    .filter(c => !["Unknown", "Outside/Ties", ""].includes(c) && !c.startsWith("I live or work outside")).length;

  // ── Q5 Sentiment ─────────────────────────────────────────────────────────
  const SENTIMENTS = [
    { key: "Excellent", label: "Excellent — glad to be here",   color: "#2E7D32" },
    { key: "Good",      label: "Good — a few things to change", color: "#6B9E6B" },
    { key: "Fair",      label: "Fair — concerns about direction",color: "#D4834A" },
    { key: "Poor",      label: "Poor — considering leaving",     color: "#B94040" },
  ];
  const sentimentData = SENTIMENTS.map(s => ({
    name:  s.label,
    value: startsWith(COL.Q5_SENTIMENT, s.key),
    color: s.color,
  }));

  // ── Q6 Stay plans ─────────────────────────────────────────────────────────
  const STAY_LABELS = ["Yes, definitely", "Probably yes", "Not sure", "No"];
  const stayData = STAY_LABELS.map(label => ({
    label,
    n: rows.filter(r => (r[COL.Q6_STAY] || "").startsWith(label)).length,
  }));

  // ── Q7 Community problems ─────────────────────────────────────────────────
  const Q7_ISSUES = [
    { col: COL.Q7_YOUTH,     issue: "Young people leaving"    },
    { col: COL.Q7_HOUSING,   issue: "Affordable housing"      },
    { col: COL.Q7_BUSINESS,  issue: "Keeping businesses open" },
    { col: COL.Q7_JOB,       issue: "Finding a good job"      },
    { col: COL.Q7_CHILDCARE, issue: "Reliable childcare"      },
    { col: COL.Q7_TRAINING,  issue: "Job training / education"},
    { col: COL.Q7_INTERNET,  issue: "Internet access"         },
    { col: COL.Q7_WATER,     issue: "Water / sewer quality"   },
  ];
  const problemData = Q7_ISSUES.map(({ col, issue }) => ({
    issue,
    serious:  count(col, "Serious problem"),
    moderate: count(col, "Moderate problem"),
    minor:    count(col, "Minor problem"),
    none:     count(col, "Not a problem"),
  })).sort((a, b) => b.serious - a.serious);

  // ── Q18 5-year priorities ─────────────────────────────────────────────────
  const ranked1 = col => rows.filter(r => (r[col] || "").includes("1 (")).length;
  const priorityData = [
    { col: COL.Q18_LIVING, area: "Better place to live\n(housing, childcare, quality of life)" },
    { col: COL.Q18_JOBS,   area: "Grow businesses\n& create more jobs"                          },
    { col: COL.Q18_PADD,   area: "Clear, accountable\nPADD plan"                                },
  ]
    .map(({ col, area }) => ({ area, ranked1: ranked1(col) }))
    .sort((a, b) => b.ranked1 - a.ranked1);

  // ── Stat cards ────────────────────────────────────────────────────────────
  const housingAffected   = startsWith(COL.Q12_HOUSING_IMPACT, "Yes");   // "Yes, directly" + "Yes, I know others" only
  const childcareAffected = startsWith(COL.Q13_CHILDCARE_IMPACT, "Yes"); // excludes "Not sure / doesn't apply"
  const successionAware   = startsWith(COL.Q10_SUCCESSION, "Yes");
  const neverHeardPADD    = startsWith(COL.Q15_FAMILIARITY, "Not familiar");
  const veryFamiliarPADD  = startsWith(COL.Q15_FAMILIARITY, "Very familiar");

  const pct = n => `${Math.round((n / N) * 100)}%`;

  const statCards = [
    { icon: "🏠", pct: pct(housingAffected),   color: C.serious,  label: "affected by housing shortage",      sub: `${housingAffected} of ${N} respondents — directly or know someone impacted` },
    { icon: "👶", pct: pct(childcareAffected),  color: C.moderate, label: "affected by childcare shortage",    sub: `${childcareAffected} of ${N} — prevented work, job change, or starting a business` },
    { icon: "🏪", pct: pct(successionAware),    color: C.gold,     label: "know a business at risk of closing",sub: `${successionAware} of ${N} know of an owner retiring with no succession plan` },
    { icon: "📣", pct: pct(neverHeardPADD),     color: C.slate,    label: "had never heard of PADD",           sub: `${neverHeardPADD} of ${N}. Only ${pct(veryFamiliarPADD)} had worked with PADD directly` },
  ];

  // ── Collection period from timestamps ────────────────────────────────────
  const dates = rows
    .map(r => new Date(r[COL.TIMESTAMP]))
    .filter(d => !isNaN(d.getTime()));
  const minDate = dates.length ? new Date(Math.min(...dates)) : null;
  const maxDate = dates.length ? new Date(Math.max(...dates)) : null;
  const fmt = d => d.toLocaleDateString("en-US", { month: "short", year: "numeric" });
  const period = minDate && maxDate
    ? (minDate.getMonth() === maxDate.getMonth() && minDate.getFullYear() === maxDate.getFullYear()
        ? fmt(minDate)
        : `${fmt(minDate)} – ${fmt(maxDate)}`)
    : "Survey";

  return { N, countyCount, countyData, sentimentData, stayData, problemData, priorityData, statCards, period };
}

// ── Tooltips ──────────────────────────────────────────────────────────────────
function ProblemTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  const total = payload.reduce((s, p) => s + (p.value || 0), 0);
  return (
    <div style={{ background: C.white, border: `1px solid ${C.sand}`, borderRadius: 6, padding: "10px 14px", fontSize: 12 }}>
      <strong style={{ display: "block", marginBottom: 6 }}>{label}</strong>
      {payload.map(p => (
        <div key={p.name} style={{ color: p.fill, marginBottom: 2 }}>{p.name}: {p.value}</div>
      ))}
      <div style={{ color: "#888", marginTop: 4 }}>Responses with answer: {total}</div>
    </div>
  );
}

function SentimentTooltip({ active, payload }) {
  if (!active || !payload?.length) return null;
  const d = payload[0];
  return (
    <div style={{ background: C.white, border: `1px solid ${C.sand}`, borderRadius: 6, padding: "10px 14px", fontSize: 12 }}>
      <strong>{d.name}</strong>
      <div style={{ color: d.payload.color }}>{d.value} respondents</div>
    </div>
  );
}

// ── Section wrapper ───────────────────────────────────────────────────────────
function Section({ title, subtitle, children }) {
  return (
    <div style={{ marginBottom: 40 }}>
      <h3 style={{ color: C.navy, marginBottom: 2, fontSize: "1.05rem" }}>{title}</h3>
      {subtitle && <p style={{ color: "#666", fontSize: 12, margin: "0 0 16px" }}>{subtitle}</p>}
      {children}
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────
export default function SurveyDashboard() {
  const [result, setResult]   = useState(null);
  const [loading, setLoading] = useState(true);
  const [fetchErr, setFetchErr] = useState(null);

  useEffect(() => {
    fetch(SHEET_CSV_URL)
      .then(r => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.text();
      })
      .then(text => {
        const rows = parseCSV(text);
        const data = aggregate(rows.slice(1)); // skip header row
        setResult(data);
        setLoading(false);
      })
      .catch(err => {
        setFetchErr(err.message);
        setLoading(false);
      });
  }, []);

  if (loading) {
    return (
      <div className="chapter">
        <div className="chapter-content" style={{ textAlign: "center", padding: "60px 0", color: "#888" }}>
          Loading survey data…
        </div>
      </div>
    );
  }

  if (fetchErr || !result) {
    return (
      <div className="chapter">
        <div className="chapter-content">
          <h2>Nebraska Panhandle Community Survey</h2>
          <div style={{ background: "#FDE8E8", border: "1px solid #f5c6cb", borderRadius: 8, padding: "16px 20px", color: "#B94040", fontSize: 13 }}>
            <strong>Could not load live survey data.</strong> Make sure the Google Sheet is set to "Anyone with the link can view."
            {fetchErr && <span> ({fetchErr})</span>}
          </div>
        </div>
      </div>
    );
  }

  const { N, countyCount, countyData, sentimentData, stayData, problemData, priorityData, statCards, period } = result;
  const stayYes = stayData.find(d => d.label === "Yes, definitely")?.n + stayData.find(d => d.label === "Probably yes")?.n;

  return (
    <div className="chapter">
      <div className="chapter-content">

        {/* ── Header ── */}
        <h2>Nebraska Panhandle Community Survey</h2>
        <div style={{
          background: C.navy, color: C.white, borderRadius: 10,
          padding: "20px 28px", marginBottom: 32,
          display: "flex", gap: 40, flexWrap: "wrap", alignItems: "center",
        }}>
          <div style={{ textAlign: "center" }}>
            <div style={{ fontSize: "2.4rem", fontWeight: 800, color: C.goldLt, lineHeight: 1 }}>{N}</div>
            <div style={{ fontSize: 12, color: "#adc5c8", marginTop: 4 }}>Total Responses</div>
          </div>
          <div style={{ textAlign: "center" }}>
            <div style={{ fontSize: "2.4rem", fontWeight: 800, color: C.goldLt, lineHeight: 1 }}>{countyCount}</div>
            <div style={{ fontSize: 12, color: "#adc5c8", marginTop: 4 }}>Counties Represented</div>
          </div>
          <div style={{ textAlign: "center" }}>
            <div style={{ fontSize: "1.4rem", fontWeight: 800, color: C.goldLt, lineHeight: 1 }}>{period}</div>
            <div style={{ fontSize: 12, color: "#adc5c8", marginTop: 4 }}>Collection Period</div>
          </div>
          <div style={{ flex: 1, minWidth: 200, fontSize: 13, color: "#cdd8da", lineHeight: 1.6 }}>
            PADD conducted this regional survey to gather direct input from Panhandle residents,
            business owners, and stakeholders on economic development priorities and quality-of-life needs.
            Results inform the goals of this 2026–2031 CEDS.
          </div>
        </div>

        {/* ── Stat cards ── */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 16, marginBottom: 40 }}>
          {statCards.map(s => (
            <div key={s.label} style={{
              background: C.white, border: `1px solid ${C.sand}`,
              borderTop: `4px solid ${s.color}`, borderRadius: 8, padding: "18px 20px",
            }}>
              <div style={{ fontSize: 28, marginBottom: 6 }}>{s.icon}</div>
              <div style={{ fontSize: "1.9rem", fontWeight: 800, color: s.color, lineHeight: 1 }}>{s.pct}</div>
              <div style={{ fontWeight: 600, fontSize: 13, color: C.navy, margin: "4px 0" }}>{s.label}</div>
              <div style={{ fontSize: 11, color: "#777" }}>{s.sub}</div>
            </div>
          ))}
        </div>

        {/* ── Q5 Overall sentiment ── */}
        <Section
          title="Q5 — Overall sentiment about the Panhandle"
          subtitle="How would you describe the Panhandle as a place to live and work right now?"
        >
          <div style={{ display: "flex", gap: 32, flexWrap: "wrap", alignItems: "center" }}>
            <ResponsiveContainer width="100%" height={240} style={{ flex: "0 0 280px", maxWidth: 280 }}>
              <PieChart>
                <Pie data={sentimentData} cx="50%" cy="50%" innerRadius={55} outerRadius={100} paddingAngle={2} dataKey="value">
                  {sentimentData.map((d, i) => <Cell key={i} fill={d.color} />)}
                </Pie>
                <Tooltip content={<SentimentTooltip />} />
              </PieChart>
            </ResponsiveContainer>
            <div style={{ flex: 1, minWidth: 200 }}>
              {sentimentData.map(d => (
                <div key={d.name} style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
                  <div style={{ width: 14, height: 14, borderRadius: 3, background: d.color, flexShrink: 0 }} />
                  <div style={{ flex: 1, fontSize: 13 }}>{d.name}</div>
                  <div style={{ fontWeight: 700, color: d.color, fontSize: 15 }}>
                    {d.value} <span style={{ fontSize: 11, color: "#999", fontWeight: 400 }}>({Math.round((d.value / N) * 100)}%)</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </Section>

        {/* ── Q7 Community problems ── */}
        <Section
          title="Q7 — How much of a problem is each issue right now?"
          subtitle="Number of respondents rating each as Serious, Moderate, Minor, or Not a problem."
        >
          <ResponsiveContainer width="100%" height={320}>
            <BarChart data={problemData} layout="vertical" margin={{ left: 180, right: 20, top: 4, bottom: 4 }}>
              <CartesianGrid strokeDasharray="3 3" horizontal={false} />
              <XAxis type="number" domain={[0, N]} tick={{ fontSize: 11 }} />
              <YAxis type="category" dataKey="issue" tick={{ fontSize: 12 }} width={175} />
              <Tooltip content={<ProblemTooltip />} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Bar dataKey="serious"  name="Serious problem"  stackId="a" fill={C.serious}  />
              <Bar dataKey="moderate" name="Moderate problem" stackId="a" fill={C.moderate} />
              <Bar dataKey="minor"    name="Minor problem"    stackId="a" fill={C.minor}    />
              <Bar dataKey="none"     name="Not a problem"    stackId="a" fill={C.none}     radius={[0,4,4,0]} />
            </BarChart>
          </ResponsiveContainer>
        </Section>

        {/* ── Q18 5-year priorities ── */}
        <Section
          title="Q18 — What should the Panhandle prioritize over the next 5 years?"
          subtitle="Number of respondents who ranked each area as their #1 priority."
        >
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={priorityData} margin={{ left: 16, right: 32, top: 4, bottom: 24 }}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} />
              <XAxis dataKey="area" tick={{ fontSize: 11, whiteSpace: "pre" }} interval={0} />
              <YAxis domain={[0, N]} tick={{ fontSize: 11 }} />
              <Tooltip formatter={v => [`${v} respondents (${Math.round((v / N) * 100)}%)`, "Ranked #1"]} />
              <Bar dataKey="ranked1" name="Ranked #1 priority" radius={[4,4,0,0]}>
                {priorityData.map((_, i) => <Cell key={i} fill={[C.gold, C.slate, C.none][i]} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </Section>

        {/* ── Q6 Plan to stay ── */}
        <Section
          title="Q6 — Do you plan to still be living or working in the Panhandle in 5 years?"
          subtitle={`${Math.round((stayYes / N) * 100)}% plan to stay (definitely or probably).`}
        >
          <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
            {stayData.map(d => {
              const pct = Math.round((d.n / N) * 100);
              const col = { "Yes, definitely": "#2E7D32", "Probably yes": "#6B9E6B", "Not sure": "#D4834A", "No": C.serious }[d.label];
              return (
                <div key={d.label} style={{
                  background: C.white, border: `1px solid ${C.sand}`,
                  borderTop: `3px solid ${col}`, borderRadius: 8,
                  padding: "14px 20px", minWidth: 130, textAlign: "center",
                }}>
                  <div style={{ fontSize: "1.8rem", fontWeight: 800, color: col }}>{d.n}</div>
                  <div style={{ fontSize: 11, color: "#888" }}>{pct}%</div>
                  <div style={{ fontSize: 12, color: C.navy, fontWeight: 600, marginTop: 4 }}>{d.label}</div>
                </div>
              );
            })}
          </div>
        </Section>

        {/* ── Q1 County distribution ── */}
        <Section title="Q1 — County distribution" subtitle="Which county do you live or work in?">
          <ResponsiveContainer width="100%" height={Math.max(220, countyData.length * 28)}>
            <BarChart data={countyData} layout="vertical" margin={{ left: 110, right: 40, top: 4, bottom: 4 }}>
              <CartesianGrid strokeDasharray="3 3" horizontal={false} />
              <XAxis type="number" domain={[0, Math.max(...countyData.map(d => d.n)) + 2]} tick={{ fontSize: 11 }} />
              <YAxis type="category" dataKey="county" tick={{ fontSize: 12 }} width={105} />
              <Tooltip formatter={v => [`${v} (${Math.round((v / N) * 100)}%)`, "Responses"]} />
              <Bar dataKey="n" name="Responses" fill={C.slate} radius={[0,4,4,0]} />
            </BarChart>
          </ResponsiveContainer>
        </Section>

        {/* ── Selected open-ended responses ── */}
        <Section
          title="Selected open-ended responses"
          subtitle="Q21 — What else is important to you about the future of the Panhandle that this survey didn't ask about?"
        >
          {[
            { quote: "Keeping small towns with crumbling infrastructure alive. Small businesses are being run out by national chains, which may benefit larger cities like Scottsbluff but hurts everyone else.", attr: "Morrill County resident" },
            { quote: "We need more people, but if we don't have anywhere for them to live they won't come. I believe that our limiting factor right now is the availability of affordable starter homes for families.", attr: "Scotts Bluff County resident" },
            { quote: "The Panhandle's main assets are Agriculture and People! We don't have other raw materials.", attr: "Scotts Bluff County resident" },
            { quote: "Making the best communities we can for the people who are here now. Building a connected web of leaders and doers who support each other and have resources to draw from.", attr: "Former Panhandle resident with ties to region" },
            { quote: "Without affordable resources for ranchers, farmers, small businesses there will be no next generation to take over. Everything is too expensive for a young person to start their own thing.", attr: "Box Butte County resident, age 18–24" },
          ].map((q, i) => (
            <blockquote key={i} style={{
              borderLeft: `3px solid ${C.gold}`, margin: "0 0 16px",
              paddingLeft: 16, fontStyle: "italic", color: C.slate, fontSize: 13, lineHeight: 1.65,
            }}>
              "{q.quote}"
              <cite style={{ display: "block", fontStyle: "normal", fontSize: 11, color: "#999", marginTop: 4 }}>
                — {q.attr}
              </cite>
            </blockquote>
          ))}
        </Section>

        <p style={{ fontSize: 11, color: "#AAA", textAlign: "right", marginTop: 8 }}>
          Data source: PADD Google Sheets survey · Updates automatically as new responses are added
        </p>

      </div>
    </div>
  );
}
